import { Delete } from '@mui/icons-material';
import {
  Alert,
  DialogActions,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { blue } from '@mui/material/colors';
import {
  getClientsBankAccounts,
  savePayments,
  useActivateConfirmingRequest,
} from 'lib/api/confirming';
import {
  BankAccountClient,
  ConfirmingRequestById,
  OwnBankAccount,
} from 'lib/api/confirming/types';
import { getCompanyPayoutGroup } from 'lib/api/payments';
import BankAccountDialog from 'lib/common/BankAccountDialog';
import NomineeExporter from 'lib/common/NomineeExporter';
import CustomButton from 'lib/designSystem/Button';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import EnhancedTable from 'lib/designSystem/Table';
import {
  formatMoney,
  showAlert,
  validatePermissions,
} from 'lib/functions/utils';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AuthState } from 'services/redux/authSlice';

import { calculateRequestDetailsEarlyPayment } from '../../../Simulator/components/Simulator';
import { mapConfirmingRequestByIdToForCreation } from '../../../Simulator/utils/mapConfirmingRequestByIdToForCreation';
import WarningActivateRequest from './components/WarningActivateRequest';

const nomineeHedearsForDb = [
  { key: 'bankAccountOriginName', label: 'Banco origen' },
  { key: 'bankAccountDestinationName', label: 'Banco destino' },
  { key: 'bankAccountDestinationNumber', label: 'N° Cuenta' },
  { key: 'bankAccountDestinationAccountType', label: 'Tipo de cuenta' },
  { key: 'bankAccountDestinationHolderName', label: 'Titular' },
  { key: 'bankAccountDestinationHolderIdentifier', label: 'Rut' },
  { key: 'amount', label: 'Monto', type: 'money' },
  { key: 'executedAt', label: 'Ejecutado', type: 'date' },
];

type PaymentGrouped = {
  amount: number;
  bankAccountDestinationId: number;
  bankAccountOriginId: number;
  cut: string;
};

export const paymentsCompressor = (
  payments: {
    bankAccountDestinationId: number;
    bankAccountOriginId: number;
    amount: number;
  }[],
) => {
  const hashMap: {
    [key: string]: {
      payment: {
        bankAccountDestinationId: number;
        bankAccountOriginId: number;
        amount: number;
      };
      count: number;
    };
  } = {};
  for (let i = 0; i < payments.length; i++) {
    const payment = payments[i];
    const key = `${payment.bankAccountDestinationId}-${payment.bankAccountOriginId}-${payment.amount}`;
    if (!hashMap[key]) {
      hashMap[key] = { payment, count: 1 };
    } else {
      hashMap[key].count += 1;
    }
  }
  return hashMap;
};

const newPaymentGrouped = {
  bankAccountDestinationId: 0,
  bankAccountOriginId: 0,
  amount: 0,
  cut: '',
};

const availableCuts = (amount: number) => [
  { id: 7000000, name: '$7.000.000' },
  { id: 5000000, name: '$5.000.000' },
  { id: amount, name: 'Sin corte' },
];

export type Client = {
  businessIdentifier: string;
  businessName: string;
  totalAmount: number;
  amountNominee: number;
  businessBankAccounts: BankAccountClient[];
  paymentGroups: PaymentGrouped[];
};

type PaymentModuleProps = {
  selectedRequest: ConfirmingRequestById;
  setOpen: (open: boolean) => void;
  parentSetUp: (id?: number) => Promise<void>;
};

const PaymentModule = (props: PaymentModuleProps) => {
  const { selectedRequest, setOpen, parentSetUp } = props;
  const dispatch = useDispatch();
  const user = useSelector((state: { auth: AuthState }) => state.auth.user);

  const [originAccounts, setOriginAccounts] = useState<OwnBankAccount[]>([]);
  const [loading, setLoading] = useState(false);
  const [payments, setPayments] = useState<any[]>([]);
  const [previousPayments, setPreviousPayments] = useState<any[]>([]);
  const [exisitingNominee, setExisitingNominee] = useState<any>(false);
  const [openExporter, setOpenExporter] = useState(false);
  const [clients, setClients] = useState<Client[]>([]);
  const [isEditing, setIsEditing] = useState(false);

  const [openActivateRequestDialog, setOpenActivateRequestDialog] =
    useState(false);

  const [openBankAccountModal, setOpenBankAccountModal] = useState(false);
  const [
    businessIdentifierForAccountModal,
    setBusinessIdentifierForAccountModal,
  ] = useState('');

  const { mutate: activateConfirmingRequest, isPending: isActivatingRequest } =
    useActivateConfirmingRequest(dispatch);

  const handleCreateNominee = async () => {
    setLoading(true);
    try {
      const compressedPayments = paymentsCompressor(payments);
      await savePayments(dispatch, {
        compressedPayments,
        id: selectedRequest.id,
      });
      showAlert(dispatch, 'success', 'Nómina guardada correctamente');
      setIsEditing(false);
      await setUpPayments();
    } catch (error) {
      showAlert(dispatch, 'error', 'Error al guardar la nómina');
    }
    setLoading(false);
  };

  const setUpPayments = async () => {
    setLoading(true);
    try {
      const exisitingNominee = await getCompanyPayoutGroup(
        dispatch,
        'confirming-request',
        selectedRequest.id,
      );
      setPayments(exisitingNominee.payments);
      if (exisitingNominee?.payments?.length) setExisitingNominee(true);
    } catch (error) {
      showAlert(dispatch, 'error', 'Error al obtener la nómina');
    }
    setLoading(false);
  };

  const handleExport = () => {
    setOpenExporter(true);
  };

  const handleOpenBankAccountModal = (businessIdentifier: string) => {
    setBusinessIdentifierForAccountModal(businessIdentifier);
    setOpenBankAccountModal(true);
  };

  const handleGenerateNominee = () => {
    setLoading(true);
    // Revisar que todos los payment groups de cada client tengan los 4 campos completos
    const clientsWithAllFields = clients.filter((client) =>
      client.paymentGroups.every((paymentGroup) =>
        Object.values(paymentGroup).every((value) => value !== ''),
      ),
    );
    if (clientsWithAllFields.length !== clients.length) {
      showAlert(dispatch, 'error', 'Faltan campos por completar');
      setLoading(false);
      return;
    }
    const paymentGenerated = [];
    for (let i = 0; i < clients.length; i++) {
      const client = clients[i];
      const paymentGroup = client.paymentGroups;
      for (let j = 0; j < paymentGroup.length; j++) {
        const payment = paymentGroup[j];
        let remainingAmount = Number(payment.amount);
        const account = client.businessBankAccounts.find(
          (account) => account.id === payment.bankAccountDestinationId,
        );
        const originAccount = originAccounts.find(
          (account) => account.id === payment.bankAccountOriginId,
        );
        if (!account || !originAccount) continue;
        while (remainingAmount > 0) {
          const amountToDeposit =
            remainingAmount > Number(payment.cut)
              ? Number(payment.cut)
              : remainingAmount;
          paymentGenerated.push({
            businessDestinationName: client.businessName,
            bankAccountDestinationId: payment.bankAccountDestinationId,
            bankAccountOriginId: payment.bankAccountOriginId,
            bankAccountDestinationName: account.bankName,
            bankAccountDestinationNumber: account.accountNumber,
            bankAccountDestinationAccountType: account.accountType,
            bankAccountDestinationHolderName: account.holderName,
            bankAccountDestinationHolderIdentifier: account.holderIdentifier,
            bankAccountOriginName: originAccount.bankName,
            bankName: account.bankName,
            accountNumber: account.accountNumber,
            accountType: account.accountType,
            holderName: account.holderName,
            holderIdentifier: account.holderIdentifier,
            businessIdentifier: account.businessIdentifier,
            amount: amountToDeposit,
            operationId: selectedRequest.id,
            executedAt: null,
          });
          remainingAmount -= amountToDeposit;
        }
      }
    }
    setPayments(paymentGenerated);
    setLoading(false);
  };

  const handleAddTransfer = (businessIdentifier: string) => () => {
    const clientToModify = clients.find(
      (client: Client) => client.businessIdentifier === businessIdentifier,
    );
    if (!clientToModify) return;
    clientToModify.paymentGroups = [
      ...(clientToModify.paymentGroups || []),
      structuredClone(newPaymentGrouped),
    ];
    setClients([...clients]);
  };

  const handleAmountChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    index: number,
    businessIdentifier: string,
  ) => {
    const clientToModify = clients.find(
      (client) => client.businessIdentifier === businessIdentifier,
    );
    if (!clientToModify) return;
    const value = e.target.value.split('.').join('').split('$').join('');
    const newPayments = [...clientToModify.paymentGroups];
    newPayments[index].amount = parseInt(value);
    clientToModify.paymentGroups = newPayments;
    // Sumar el amount de todos los paymentGroups
    const totalAmount = clientToModify.paymentGroups.reduce(
      (acc: number, curr: PaymentGrouped) => acc + curr.amount,
      0,
    );
    clientToModify.amountNominee = totalAmount;
    setClients([...clients]);
  };

  const fetchClientsBankAccounts = async (clientsInput?: Client[]) => {
    const clientsForRequest = clientsInput || clients;
    const clientsWithBankAccounts = await getClientsBankAccounts(dispatch, {
      clientsForRequest,
    });
    if (clientsWithBankAccounts) {
      setOriginAccounts(clientsWithBankAccounts.ownBankAccounts);
      setClients(clientsWithBankAccounts.clientsBankAccounts);
    }
  };

  const getDocumentsWithDetails = (selectedRequest: ConfirmingRequestById) => {
    if (selectedRequest.type === 'traditional') {
      return selectedRequest.documents;
    }
    const requestForCreation =
      mapConfirmingRequestByIdToForCreation(selectedRequest);
    const { documentsWithDetails } =
      calculateRequestDetailsEarlyPayment(requestForCreation);
    return documentsWithDetails;
  };

  const setUpDataForClients = async () => {
    setLoading(true);
    await setUpPayments();
    const documents = getDocumentsWithDetails(selectedRequest);
    const clients = Object.values(
      documents.reduce(
        (
          acc: Record<string, Client>,
          curr: {
            businessIdentifier: string;
            businessName: string;
            amount: number;
            amountToDeposit?: number;
          },
        ) => {
          const id = curr.businessIdentifier;
          if (!acc[id]) {
            acc[id] = {
              businessIdentifier: id,
              businessName: curr.businessName,
              totalAmount: 0,
              amountNominee: 0,
              businessBankAccounts: [],
              paymentGroups: [],
            };
          }
          acc[id].totalAmount += curr.amountToDeposit || curr.amount;
          return acc;
        },
        {},
      ),
    );
    setClients(clients);
    await fetchClientsBankAccounts(clients);
    setLoading(false);
  };

  useEffect(() => {
    void setUpDataForClients();
  }, []);

  const disableGenerate =
    !clients.length ||
    !clients.every((client) => client.businessBankAccounts) ||
    !clients.every((client) => client.amountNominee === client.totalAmount);

  const handleEditNominee = () => {
    setPreviousPayments([...payments]);
    setIsEditing(true);
    setPayments([]);
  };

  const handleActivateRequest = () => {
    setOpenActivateRequestDialog(false);
    activateConfirmingRequest(selectedRequest.id, {
      onSuccess: () => {
        showAlert(dispatch, 'success', 'Solicitud activada correctamente');
        void parentSetUp(selectedRequest.id);
      },
      onError: () => {
        showAlert(dispatch, 'error', 'Error al activar la solicitud');
      },
    });
  };

  const somePaymentExecuted = payments.some((x) => x.executedAt);

  const nomineeFullAmount = payments.reduce(
    (acc, curr) => acc + curr.amount,
    0,
  );
  const clientsFullAmount = clients.reduce(
    (acc, curr) => acc + curr.totalAmount,
    0,
  );
  const cantSaveNominee =
    !payments.length || nomineeFullAmount !== clientsFullAmount;

  const showActivateRequest =
    !isEditing &&
    exisitingNominee &&
    nomineeFullAmount === clientsFullAmount &&
    (selectedRequest.status === 'IN_TRANSFER' ||
      selectedRequest.status === 'En giro') &&
    validatePermissions(
      user,
      'financing_confirming_activate_without_payouts_executed',
    );

  const isLoading = loading || isActivatingRequest;

  if (isLoading) return <CenteredCircularProgress type="dialog" />;

  return (
    <div>
      {!payments?.length ? (
        <div>
          <div
            style={{
              padding: '20px 0px',
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            <Typography fontWeight={600} variant="h6">
              Generación nómina
            </Typography>
            <CustomButton
              color="primary"
              onClick={handleGenerateNominee}
              disabled={disableGenerate}
            >
              Generar nómina
            </CustomButton>
          </div>
          {!originAccounts.length && (
            <Typography variant="body1" padding="20px 0px">
              No tienes cuentas registradas para tu empresa. Crea una para poder
              generar una nómina.
            </Typography>
          )}
          {originAccounts.length &&
            clients.map((client) => (
              <div key={client.businessIdentifier}>
                <div
                  style={{ display: 'flex', justifyContent: 'space-between' }}
                >
                  <div
                    style={{
                      padding: '20px 0px',
                      display: 'flex',
                      gap: '20px',
                    }}
                  >
                    <Typography variant="body1" fontWeight={600}>
                      {client.businessName}
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      Monto solicitud: {formatMoney(client.totalAmount)}
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      Monto en nómina: {formatMoney(client.amountNominee || 0)}
                    </Typography>
                  </div>
                  {client?.businessBankAccounts?.length > 0 && (
                    <div>
                      <CustomButton
                        color="primary"
                        onClick={handleAddTransfer(client.businessIdentifier)}
                      >
                        Agregar
                      </CustomButton>
                    </div>
                  )}
                </div>
                {client?.businessBankAccounts?.length == 0 && (
                  <div style={{ display: 'flex', gap: '5px' }}>
                    <Typography variant="body1" padding="20px 0px">
                      No tienes cuentas registradas para este cliente. Crea una
                      para poder generar una nómina.
                    </Typography>
                    <Typography
                      variant="body1"
                      padding="20px 0px"
                      color={blue[900]}
                      sx={{
                        cursor: 'pointer',
                        '&:hover': {
                          textDecoration: 'underline',
                        },
                      }}
                      onClick={() => {
                        handleOpenBankAccountModal(client.businessIdentifier);
                      }}
                    >
                      Acá
                    </Typography>
                  </div>
                )}
                {client?.businessBankAccounts?.length > 0 &&
                  client?.paymentGroups?.map((_x, index: number) => (
                    <Grid
                      key={`${index}-${client.businessIdentifier}`}
                      container
                      spacing={2}
                      alignItems="center"
                      rowSpacing={4}
                      style={{ padding: '5px 0px' }}
                    >
                      <Grid item xs={2}>
                        <TextField
                          fullWidth
                          label="Monto"
                          value={formatMoney(
                            client.paymentGroups[index].amount,
                          )}
                          onChange={(e) =>
                            handleAmountChange(
                              e,
                              index,
                              client.businessIdentifier,
                            )
                          }
                        />
                      </Grid>
                      <Grid item xs={3}>
                        <FormControl fullWidth>
                          <InputLabel id="filter-label">
                            {'Cuenta Origen'}
                          </InputLabel>
                          <Select
                            labelId="filter-label"
                            id="Filtrar por estado"
                            fullWidth
                            value={
                              client.paymentGroups[index].bankAccountOriginId
                            }
                            onChange={(e) => {
                              const clientToModify = clients.find(
                                (clientFromClients) =>
                                  clientFromClients.businessIdentifier ===
                                  client.businessIdentifier,
                              );
                              if (!clientToModify) return;
                              const newPayments = [
                                ...clientToModify.paymentGroups,
                              ];
                              newPayments[index].bankAccountOriginId = Number(
                                e.target.value,
                              );
                              clientToModify.paymentGroups = newPayments;
                              setClients([...clients]);
                            }}
                            label={'Cuenta bancaria'}
                          >
                            {originAccounts.map((x, index: number) => (
                              <MenuItem
                                key={`${index}-${client.businessIdentifier}`}
                                value={x.id}
                              >
                                {x.bankName} ({x.accountNumber})
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={3}>
                        <FormControl fullWidth>
                          <InputLabel id="filter-label">
                            {'Cuenta destino'}
                          </InputLabel>
                          <Select
                            labelId="filter-label"
                            id="Filtrar por estado"
                            fullWidth
                            value={
                              client.paymentGroups[index]
                                .bankAccountDestinationId
                            }
                            onChange={(e) => {
                              const clientToModify = clients.find(
                                (clientFromClients) =>
                                  clientFromClients.businessIdentifier ===
                                  client.businessIdentifier,
                              );
                              if (!clientToModify) return;
                              const newPayments = [
                                ...clientToModify.paymentGroups,
                              ];
                              newPayments[index].bankAccountDestinationId =
                                Number(e.target.value);
                              clientToModify.paymentGroups = newPayments;
                              setClients([...clients]);
                            }}
                            label={'Cuenta bancaria'}
                          >
                            {client?.businessBankAccounts?.map(
                              (x, index: number) => (
                                <MenuItem
                                  key={`${index}-${client.businessIdentifier}`}
                                  value={x.id}
                                >
                                  {x.bankName} ({x.accountNumber})
                                </MenuItem>
                              ),
                            )}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={2}>
                        <FormControl fullWidth>
                          <InputLabel id="filter-label">{'Corte'}</InputLabel>
                          <Select
                            labelId="filter-label"
                            id="Filtrar por estado"
                            fullWidth
                            value={client.paymentGroups[index].cut}
                            onChange={(e) => {
                              const clientToModify = clients.find(
                                (clientFromClients) =>
                                  clientFromClients.businessIdentifier ===
                                  client.businessIdentifier,
                              );
                              if (!clientToModify) return;
                              const newPayments = [
                                ...clientToModify.paymentGroups,
                              ];
                              newPayments[index].cut = e.target.value;
                              clientToModify.paymentGroups = newPayments;
                              setClients([...clients]);
                            }}
                            label={'Corte'}
                          >
                            {availableCuts(
                              client.paymentGroups[index].amount,
                            ).map(
                              (
                                x: { id: number; name: string },
                                index: number,
                              ) => (
                                <MenuItem
                                  key={`${index}-${client.businessIdentifier}`}
                                  value={x.id}
                                >
                                  {x.name}
                                </MenuItem>
                              ),
                            )}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={2}>
                        <Grid container>
                          <Grid item xs={9}>
                            <Typography textAlign={'center'}>
                              {client.paymentGroups[index].amount &&
                              client.paymentGroups[index].cut
                                ? ` N° de trasferencias: ${Math.ceil(
                                    client.paymentGroups[index].amount /
                                      Number(client.paymentGroups[index].cut),
                                  )}`
                                : 'Complete los campos'}{' '}
                            </Typography>
                          </Grid>
                          <Grid item xs={3}>
                            <IconButton
                              onClick={() => {
                                const clientToModify = clients.find(
                                  (clientFromClients) =>
                                    clientFromClients.businessIdentifier ===
                                    client.businessIdentifier,
                                );
                                if (!clientToModify) return;
                                const newPayments = [
                                  ...clientToModify.paymentGroups,
                                ];
                                newPayments.splice(index, 1);
                                clientToModify.paymentGroups = newPayments;
                                setClients([...clients]);
                              }}
                            >
                              <Delete />
                            </IconButton>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  ))}
              </div>
            ))}
        </div>
      ) : (
        <div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              padding: '10px 0px',
            }}
          >
            <Typography fontWeight={600} variant="h6">
              {'Nómina'}
            </Typography>
            {!exisitingNominee && (
              <CustomButton color="secondary" onClick={() => setPayments([])}>
                Volver
              </CustomButton>
            )}
            {exisitingNominee && (
              <div>
                <CustomButton color="primary" onClick={() => handleExport()}>
                  Exportar
                </CustomButton>
              </div>
            )}
          </div>
          <Typography variant="body1" padding="20px 0px">
            Acá podrás ver todas las transferencias que se realizarán en la
            nómina.
          </Typography>
          {payments.length && cantSaveNominee && (
            <Alert severity="error" variant="outlined">
              La suma de los montos de la nómina no coincide con el monto total
              a girar de la solicitud.
            </Alert>
          )}
          <EnhancedTable
            dataHeaders={nomineeHedearsForDb}
            data={payments}
            rowsPerPageDefault={10}
            finalSumRow
          />
          {exisitingNominee &&
            payments.some((x: any) => !x.executedAt) &&
            validatePermissions(user, 'financing_payout_view') && (
              <Typography variant="body1" padding="20px 0px">
                Para ejecutar los pagos, dirígete a{' '}
                <a href="/finance/payments/out">Pagos enviados</a>
              </Typography>
            )}
        </div>
      )}
      <DialogActions>
        <CustomButton color="secondary" onClick={() => setOpen(false)}>
          Salir
        </CustomButton>
        {exisitingNominee && !isEditing && (
          <CustomButton
            color="primary"
            onClick={() => handleEditNominee()}
            disabled={somePaymentExecuted}
          >
            Editar nómina
          </CustomButton>
        )}
        {isEditing && (
          <CustomButton
            color="secondary"
            onClick={() => {
              setPayments([...previousPayments]);
              setIsEditing(false);
              setPreviousPayments([]);
            }}
          >
            Cancelar edición
          </CustomButton>
        )}
        {(!exisitingNominee && payments.length) ||
        (payments.length && isEditing) ? (
          <CustomButton
            color="primary"
            onClick={() => handleCreateNominee()}
            disabled={cantSaveNominee || isLoading}
          >
            Guardar Nómina
          </CustomButton>
        ) : null}
        {showActivateRequest && (
          <CustomButton
            color="success"
            onClick={() => setOpenActivateRequestDialog(true)}
            disabled={isLoading}
          >
            Activar solicitud
          </CustomButton>
        )}
      </DialogActions>
      <NomineeExporter
        open={openExporter}
        setOpen={setOpenExporter}
        payments={payments}
        id={selectedRequest.id.toString()}
      />
      {openBankAccountModal && businessIdentifierForAccountModal && (
        <BankAccountDialog
          open={openBankAccountModal}
          setOpen={setOpenBankAccountModal}
          businessId={businessIdentifierForAccountModal}
          handleSuccessExit={() => void setUpDataForClients()}
        />
      )}
      {openActivateRequestDialog && (
        <WarningActivateRequest
          open={openActivateRequestDialog}
          setOpen={setOpenActivateRequestDialog}
          handleActivateRequest={handleActivateRequest}
        />
      )}
    </div>
  );
};

export default PaymentModule;
